import { useSelector } from 'react-redux'

import { dataSourceContextsSelector } from 'apps/formsCreationKit/store/selectors/dataSourceContexts'

import { guidNumber } from 'apps/studio/lib/utils/entities'

import { DataIcon } from '@gocanvas/gcds/icons'
import LiveDataIcon from 'apps/builder/assets/icons/live-data.svg'
import UpgradeIcon from 'apps/studio/assets/icons/Upgrade.svg'

import {
  activeEntrySelector,
  entriesSelector,
  referenceDataFileListSelector,
  eligibleDependentEntriesSelector,
  liveDataSourcesSelector,
  dataFileSourcesSelector,
} from 'apps/formsCreationKit/store/selectors/entities'

import { eligibleForReferenceData } from 'apps/builder/lib/utils'
import { isTrueStringMatch } from 'apps/builder/lib/utils'

export const REFERENCE_DATA_PANEL = 'Reference Data'

const hasDataSource = data_source_context_id =>
  data_source_context_id != null && data_source_context_id !== 'None'

const sortByLabel = (a, b) => a.label.localeCompare(b.label)

const refDataColumnCheck =
  (referenceDataId, refDataFiles) =>
  ({
    reference_data_id, // potential parent entry, B
    reference_data_column, // potential parent entry, B
  }) => {
    // a potential parent must have a reference file and column chosen
    if (!reference_data_id || !reference_data_column) {
      return false
    }
    // if that was true and the file is the same, gtg
    if (isTrueStringMatch(referenceDataId, reference_data_id)) {
      return true
    }

    // looking up all headers for A
    const { headers = [] } =
      refDataFiles.find(
        file => reference_data_id && file.id === referenceDataId
      ) || {}

    if (!headers.length) {
      return false
    }

    // B's column name must appear in A's file
    return headers.some(col => col === reference_data_column)
  }

const liveDataColumnCheck =
  (liveDataSourceId, liveDataSources) =>
  ({
    data_source_context_id, // potential parent entry, B
    live_data_source_column, // potential parent entry, B
  }) => {
    // a potential parent must have a live data source and column chosen

    if (!hasDataSource(data_source_context_id) || !live_data_source_column) {
      return false
    }
    // if that was true and the file is the same, gtg
    if (isTrueStringMatch(liveDataSourceId, data_source_context_id)) {
      return true
    }

    // looking up all headers for A
    const { mappings = [] } =
      liveDataSources.find(
        file => hasDataSource(data_source_context_id) && isTrueStringMatch(file.id, liveDataSourceId)
      ) || {}

    const headers = mappings.map(m => m.name)

    if (!headers.length) {
      return false
    }
    // B's column name must appear in A's file
    return headers.some(col => col === live_data_source_column)
  }

// per builder 2, for A to depend on B when B has a different
// file, B's column name must appear in A's file
const columnCheck = (
  {
    reference_data_id: referenceDataId, // active entry, A
    live_data_source_id: liveDataSourceId,
  },
  refDataFiles
) =>
  referenceDataId
    ? refDataColumnCheck(referenceDataId, refDataFiles)
    : liveDataColumnCheck(liveDataSourceId, refDataFiles)

const RefDataFileSelect = {
  label: 'Source',
  settingType: 'selectInput',
  property: 'reference_data_id',
  size: 'large',
  computedProperty: ({ data, option }) => {
    if (!option) {
      const { reference_data_id, live_data_source_id } = data
      if (reference_data_id) return 'reference_data_id'
      if (live_data_source_id) return 'live_data_source_id'
    }

    if (option && option.type === 'live_data') {
      return 'live_data_source_id'
    }

    return 'reference_data_id'
  },
  dynamicChoices: () => {
    const refData = useSelector(dataFileSourcesSelector)
    const entry = useSelector(activeEntrySelector) || {}

    return [
      {
        label: 'None',
        realValue: null,
        value: 'None',
        type: 'none',
        additionalProperties: {
          live_data_source_id: null,
          reference_data_id: null,
          reference_data_column: null,
          live_data_source_column: null,
          data_source_context_id: null,
          dependent_entry: null,
        },
      },
      ...refData.map(({ id, name, type }) => {
        return {
          label: name,
          value: id,
          IconComponent: type === 'reference_data' ? DataIcon : LiveDataIcon,
          additionalProperties: {
            live_data_source_id: type === 'live_data' ? id : null,
            data_source_context_id:
              type === 'live_data'
                ? guidNumber()
                : null,
            reference_data_id: type === 'reference_data' ? id : null,
            reference_data_column: null,
            live_data_source_column: null,
            dependent_entry: null,
            check_data_source_context: type === 'live_data',
            dataContextLabel: `${entry.label} Context`,
          },
        }
      }),
    ]
  },
}

const RefDataColumnSelect = {
  label: 'Column',
  settingType: 'selectInput',
  size: 'large',
  property: 'reference_data_column',
  dynamicChoices: () => {
    const entry = useSelector(activeEntrySelector)
    const { reference_data_id } = entry
    const refDataFiles = useSelector(referenceDataFileListSelector)

    const { headers: refDataColumns = [] } = reference_data_id
      ? refDataFiles.find(({ id }) => id == reference_data_id) || {}
      : {}

    return [
      {
        label: 'None',
        realValue: null,
        value: '',
        additionalProperties: {
          dependent_entry: null,
        },
      },
      ...refDataColumns
        .map(columnData => ({
          label: columnData,
          value: columnData,
        }))
        .sort(sortByLabel),
    ]
  },
  isDisabled: ({ reference_data_id }) => !reference_data_id,
  showInput: ({ live_data_source_id, data_source_context_id }) => {
    return !live_data_source_id && !hasDataSource(data_source_context_id)
  },
}


const LiveDataColumnSelect = {
  label: 'Column',
  settingType: 'selectInput',
  size: 'large',
  property: 'live_data_source_column',
  dynamicChoices: () => {
    const entry = useSelector(activeEntrySelector)
    const { data_source_context_id, live_data_source_id } = entry
    const refDataFiles = useSelector(liveDataSourcesSelector)
    const { mappings: refDataColumns = [] } = hasDataSource(data_source_context_id)
      ? refDataFiles.find(source => isTrueStringMatch(source.id, live_data_source_id)) || {}
      : {}
    return refDataColumns
      .map(({ id, name }) => ({
        label: name,
        value: name,
      }))
      .sort(sortByLabel)
  },
  isDisabled: ({ data_source_context_id }) => !hasDataSource(data_source_context_id),
  showInput: ({
    reference_data_id,
    data_source_context_id,
    live_data_source_id,
  }) => {
    return !reference_data_id && live_data_source_id && hasDataSource(data_source_context_id)
  },
}

const RefDataScreenSelect = {
  label: 'Screen',
  settingType: 'selectInput',
  size: 'large',
  property: 'dependent_entry',
  nestedProp: 'sheet_id',
  dynamicChoices: () => {
    const activeEntry = useSelector(activeEntrySelector)
    const refDataFiles = useSelector(dataFileSourcesSelector)

    const { guid: activeEntryGuid, label, live_data_source_id } = activeEntry
    const dependentEntryScreens = useSelector(eligibleDependentEntriesSelector)
    const validScreens = []
    const hasColumnInCommon = columnCheck(activeEntry, refDataFiles)
    // First, find all screens that have at least one valid entry
    dependentEntryScreens.forEach(({ id, entries, screenLabel }) => {
      const hasValidEntries = entries.some(entry => {
        const { guid, dependent_entry, label } = entry

        return (
          guid !== activeEntryGuid && // can't link to a field to itself
          eligibleForReferenceData(entry) && // only include entries eligible for reference data
          hasColumnInCommon(entry) &&
          (dependent_entry == null ||
            (dependent_entry.guid && dependent_entry.guid !== activeEntryGuid)) // can't link to a field that already depends on this one
        )
      })

      // dedupes sheets
      if (hasValidEntries) {
        validScreens.push({
          label: screenLabel,
          value: id,
        })
      }
    })

    const extraProperties = live_data_source_id ? {
      live_data_source_id,
      data_source_context_id: guidNumber(),
      check_data_source_context: true,
      dataContextLabel: `${label} Context`,
    } : {}

    return validScreens.length
      ? [
          {
            label: 'None',
            value: '',
            additionalProperties: {
              dependent_entry: null,
              ...extraProperties,
            },
          },
          ...validScreens,
        ]
      : []
  },
  isDisabled: ({ reference_data_column, live_data_source_column }) =>
    !reference_data_column && !live_data_source_column,
}

const RefDataFieldSelect = {
  label: 'Field',
  settingType: 'selectInput',
  size: 'large',
  property: 'dependent_entry',
  nestedProp: 'guid',
  dynamicChoices: () => {
    const entry = useSelector(activeEntrySelector)
    const { guid: activeEntryGuid, dependent_entry, live_data_source_id, data_source_context_id: activeDataSourceContextId } = entry
    const { sheet_id: selectedSheet } = dependent_entry || {}
    const dependentEntryScreens = useSelector(eligibleDependentEntriesSelector)
    const refDataFiles = useSelector(dataFileSourcesSelector)
    const hasColumnInCommon = columnCheck(entry, refDataFiles)
    // only consider the current sheet
    // confirm that this works for table entries
    const currentSheetEntries =
      dependentEntryScreens.find(({ id }) =>
        isTrueStringMatch(id, selectedSheet)
      )?.entries || []
    const validDependentEntries = currentSheetEntries
      .reduce((acc, entry) => {
        const { guid, dependent_entry, data_source_context_id, label } = entry
        const sameLiveDataSource = isTrueStringMatch(live_data_source_id, entry.live_data_source_id)
        if (
          guid !== activeEntryGuid && // can't link to a field to itself
          eligibleForReferenceData(entry) && // only include entries eligible for reference data
          hasColumnInCommon(entry) && // either the same file or a file with a matching column
          (dependent_entry == null || // can't link to a field that already depends on this one
            (dependent_entry.guid && dependent_entry.guid !== activeEntryGuid))
        ) {
          acc.push({
            label: label,
            value: guid,
            additionalNestedProperties: {
              sheet_id: selectedSheet,
              data_source_context_id: sameLiveDataSource ? data_source_context_id : activeDataSourceContextId,
            },
          })
        }
        return acc
      }, [])
      .sort(sortByLabel)

    const noneCase = {
      label: 'None',
      realValue: null,
      value: '',
      additionalNestedProperties: { sheet_id: selectedSheet },
    }

    return selectedSheet && validDependentEntries.length
      ? [noneCase, ...validDependentEntries]
      : [noneCase]
  },
  isDisabled: ({ dependent_entry }) =>
    !dependent_entry || dependent_entry.sheet_id == 'None',
}

const LiveDataParams = {
  label: 'Dynamic Parameters',
  settingType: 'dynamicParamsSelect',
  hideLabel: true,
  dynamicChoices: () => {
    const entry = useSelector(activeEntrySelector)
    const { data_source_context_id } = entry
    const dataSources = useSelector(liveDataSourcesSelector)
    const contexts = useSelector(dataSourceContextsSelector)
    const context =
      contexts.find(({ id }) => isTrueStringMatch(id, data_source_context_id)) || {}

    if (!context) {
      return []
    }

    const { dynamic_params = [] } =
      dataSources.find(ds => isTrueStringMatch(ds.id, context.live_data_source_id)) || {}
    return {
      context,
      dynamicParams: dynamic_params.map(param => ({
        ...param,
        data_source_context_id,
      })),
    }
  },
  showInput: ({ data_source_context_id }) => {
    const dataSources = useSelector(liveDataSourcesSelector)
    const contexts = useSelector(dataSourceContextsSelector)
    const context =
      contexts.find(({ id }) => isTrueStringMatch(id, data_source_context_id)) || {}

    if (!context) {
      return false
    }

    const { dynamic_params = [] } =
      dataSources.find(ds => isTrueStringMatch(ds.id, context.live_data_source_id)) || {}
    return dynamic_params.length > 0
  },
  isDisabled: () => {
    const entry = useSelector(activeEntrySelector) || {}
    const entries = useSelector(entriesSelector)
    const { dependent_entry } = entry
    const { guid: dependentEntryGuid } = dependent_entry || {}
    const dependentEntry = entries.find(
      ({ guid }) => guid === dependentEntryGuid
    ) || {} 
    return (
      dependentEntry &&
      dependentEntry.data_source_context_id === entry.data_source_context_id
    )
  },
}

export const ReferenceDataSettings = [
  RefDataFileSelect,
  RefDataColumnSelect,
  LiveDataColumnSelect,
  RefDataScreenSelect,
  RefDataFieldSelect,
  LiveDataParams,
]

const ReferenceDataUpsell = () => (
  <div className="reference-data--plg-upsell">
    <UpgradeIcon aria-hidden="true" />
    <p className="reference-data--plg-upsell--text">
      <strong>Upgrade</strong> to access Reference Data and auto-fill your
      forms, error-free, with important information (customers, parts, jobs,
      and more).
    </p>
  </div>
)

export const ReferenceDataPanel = {
  label: REFERENCE_DATA_PANEL,
  icon: DataIcon,
  settings: ReferenceDataSettings,
  plgUpsell: ReferenceDataUpsell,
}
